/**
 * Credits used this cycle (2026-10-25). One bar, in dollars: what credits
 * paid this cycle (overage plus the plan fee) against what the account had to
 * spend (that plus today's balance), then one line saying what the dollars
 * went on.
 *
 * Shown in the plan summary (Usage and Account screens) and under the meters
 * in the projects list's "Across your projects", from the same account usage
 * payload, so the screens cannot disagree.
 */
import { UsageBar } from './UsageBar';

import { t } from '../../lib/i18n/t';
import type { Usage } from '../../lib/types';

import '../../styles/components/usage.css';

/** US dollars, the currency credits are held in. The symbol is the same in both languages. */
function dollars(amount: string): string {
  return `$${amount}`;
}

/** A two-decimal dollar string from the server as whole cents, without floating point drift. */
function cents(amount: string): number {
  return Math.round(Number(amount) * 100);
}

export function CreditsSpentBar({
  spent,
  compact = false,
}: {
  spent: NonNullable<Usage['creditsSpent']>;
  /** The thin variant, beside the projects list's other compact meters. */
  compact?: boolean;
}) {
  const used = cents(spent.totalUsd);
  const available = cents(spent.availableUsd);
  return (
    <div className="plan-sum__spent">
      <UsageBar
        label={t('Credits used this cycle')}
        used={used}
        limit={used + available}
        unit="usd_cents"
        compact={compact}
        showLimitAtZero={available > 0}
        zeroSentence={t('No credits used this cycle')}
      />
      {used > 0 ? (
        <p className="plan-sum__aside">
          {t('{overage} past your allowance, {plan} for your plan', {
            overage: dollars(spent.overageUsd),
            plan: dollars(spent.planUsd),
          })}
        </p>
      ) : null}
    </div>
  );
}
